import { Helmet } from 'react-helmet-async';
import type { RestaurantResponse } from '@/features/restaurants/types';

interface RestaurantSeoHeadProps {
  restaurant: RestaurantResponse;
}

const PLATFORM_HOSTNAMES = new Set(['restroos.com', 'www.restroos.com', 'localhost', '127.0.0.1']);

function isPlatformHost(hostname: string): boolean {
  return (
    PLATFORM_HOSTNAMES.has(hostname) ||
    hostname.endsWith('.restroos.com') ||
    hostname.endsWith('.web.app') ||
    hostname.endsWith('.firebaseapp.com') ||
    hostname.endsWith('.run.app')
  );
}

/**
 * Injects per-restaurant SEO meta tags via react-helmet-async.
 *
 * Covers:
 * - Browser tab title
 * - og:title / og:description / og:image (custom cover or logo)
 * - twitter:card
 * - Canonical URL (respects custom domains)
 * - Restaurant JSON-LD structured data
 *
 * Does NOT fix social-bot previews (WhatsApp, Telegram) — those bots don't
 * execute JS and will see the static index.html tags. Fix that by adding
 * bot-detection in nginx.conf that proxies to the backend's OG endpoint.
 */
export function RestaurantSeoHead({ restaurant }: RestaurantSeoHeadProps) {
  const hostname = window.location.hostname;
  const onCustomDomain = !isPlatformHost(hostname);

  const canonicalUrl = onCustomDomain
    ? `https://${hostname}`
    : restaurant.slug
    ? `https://restroos.com/${restaurant.slug}`
    : window.location.origin;

  const title =
    restaurant.seoMetaTitle ||
    (restaurant.name ? `${restaurant.name} – Online bestellen` : 'Online bestellen');

  const description =
    restaurant.seoMetaDescription ||
    restaurant.description ||
    (restaurant.name ? `Bestellen Sie direkt bei ${restaurant.name} – schnell, einfach und ohne Provision.` : '');

  const keywords =
    typeof restaurant.seoKeywords === 'string'
      ? restaurant.seoKeywords
      : Array.isArray(restaurant.seoKeywords)
      ? (restaurant.seoKeywords as string[]).join(', ')
      : undefined;

  // Best image: cover photo > first gallery photo > logo
  const ogImage =
    restaurant.coverImageUrl ??
    restaurant.galleryPhotos?.[0] ??
    restaurant.logoUrl;

  const jsonLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Restaurant',
    name: restaurant.name,
    url: canonicalUrl,
  };
  if (description) jsonLd.description = description;
  if (restaurant.logoUrl) jsonLd.logo = restaurant.logoUrl;
  if (ogImage) jsonLd.image = ogImage;
  if (restaurant.contactPhone) jsonLd.telephone = restaurant.contactPhone;
  if (restaurant.contactEmail) jsonLd.email = restaurant.contactEmail;
  if (restaurant.currency) jsonLd.currenciesAccepted = restaurant.currency;
  if (restaurant.paymentMethods?.length) {
    jsonLd.paymentAccepted = restaurant.paymentMethods.join(', ');
  }

  const { street, city, country, postalCode, latitude, longitude, socialLinks } = restaurant;
  if (street || city || country) {
    jsonLd.address = {
      '@type': 'PostalAddress',
      ...(street && { streetAddress: street }),
      ...(city && { addressLocality: city }),
      ...(postalCode && { postalCode }),
      ...(country && { addressCountry: country }),
    };
  }
  if (latitude && longitude) {
    jsonLd.geo = { '@type': 'GeoCoordinates', latitude, longitude };
  }
  if (socialLinks) {
    const sameAs = Object.values(socialLinks).filter(Boolean);
    if (sameAs.length) jsonLd.sameAs = sameAs;
  }

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:type" content="restaurant" />
      <meta property="og:site_name" content={restaurant.name ?? 'Restaurant'} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogImage && <meta property="og:image:width" content="1200" />}
      {ogImage && <meta property="og:image:height" content="630" />}
      {ogImage && <meta property="og:image:alt" content={title} />}

      {/* Twitter */}
      <meta name="twitter:card" content={ogImage ? 'summary_large_image' : 'summary'} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      {ogImage && <meta name="twitter:image:alt" content={title} />}

      {/* Restaurant structured data */}
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
}
